import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/generated/types";
import ProjectInfoFinancialSummary from "@/components/financial/ProjectInfoFinancialSummary";
import { useProjectFinancialSummary } from "@/hooks/useProjectFinancialSummary";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import DateDisplay from "@/components/financial/DateDisplay";
import { useRemoveProjectMember } from "@/pages/projects/hooks/useRemoveProjectMember";
import { useCancelProjectInvitation } from "@/pages/projects/hooks/useCancelProjectInvitation";
import CancelProjectInvitationDialog from "./CancelProjectInvitationDialog";
import type { Project } from "@/pages/projects/hooks/useProject";

interface InfoTabProps {
  project: Project;
  projectId: string;
  canManageMembers: boolean;
  onMemberRemoved: () => void;
  onInvitationCancelled?: () => void;
}

function getStatusLabel(status: string) {
  const labels: Record<string, string> = {
    PLANNING: "Planejamento",
    IN_PROGRESS: "Em Andamento",
    COMPLETED: "Concluído",
    ON_HOLD: "Em Espera",
    CANCELLED: "Cancelado",
  };
  return labels[status] || status?.replace("_", " ") || "Desconhecido";
}

export default function InfoTab({
  project,
  projectId,
  canManageMembers,
  onMemberRemoved,
  onInvitationCancelled,
}: InfoTabProps) {
  const { currentUser, isAdmin } = useAuth();
  const { removeProjectMember, loading: removeLoading } = useRemoveProjectMember({
    onCompleted: onMemberRemoved,
  });
  const { cancelProjectInvitation, loading: cancelLoading } = useCancelProjectInvitation({
    onCompleted: onInvitationCancelled,
  });
  const showFinancialSummary = !!(project.hasFinancialAccess || isAdmin);
  const { summary, loading: summaryLoading } = useProjectFinancialSummary(projectId, {
    skip: !showFinancialSummary,
  });

  const handleRemoveMember = useCallback(
    (userId: string) => {
      removeProjectMember(projectId, userId);
    },
    [removeProjectMember, projectId]
  );

  const handleCancelInvitation = useCallback(
    (invitationId: string) => {
      cancelProjectInvitation(invitationId);
    },
    [cancelProjectInvitation]
  );

  const pendingInvitations = project.pendingInvitations ?? [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
      <div className="space-y-6">
        <Card>
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Informações do Projeto</h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Status</dt>
                <dd className="text-primary-950 dark:text-white capitalize">{getStatusLabel(project.status)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Método de Pagamento</dt>
                <dd className="text-primary-950 dark:text-white">{project.paymentMethod}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Data de Início</dt>
                <dd className="text-primary-950 dark:text-white">{project.startDate ? <DateDisplay date={project.startDate} /> : "Não definida"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Data de Término</dt>
                <dd className="text-primary-950 dark:text-white">{project.endDate ? <DateDisplay date={project.endDate} /> : "Não definida"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Acompanhamento de Construção</dt>
                <dd className="text-primary-950 dark:text-white">{project.hasConstructionTracking ? "Habilitado" : "Desabilitado"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-1">Acompanhamento Ad-hoc</dt>
                <dd className="text-primary-950 dark:text-white">{project.hasAdhocTracking ? "Habilitado" : "Desabilitado"}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {currentUser?.role === UserRole.Admin && project.internalNotes && (
          <Card>
            <CardContent className="p-8">
              <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Notas Internas</h3>
              <p className="text-primary-700 dark:text-primary-300">{project.internalNotes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="space-y-6">
        {showFinancialSummary && (
          <ProjectInfoFinancialSummary summary={summary} loading={summaryLoading} />
        )}

        <Card>
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Membros do Projeto</h3>
            <div className="space-y-3">
              {project.members?.map((member) => (
                <div key={member.id} className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0">
                  <div>
                    <div className="text-primary-950 dark:text-white font-medium">{member.fullName}</div>
                    <div className="text-sm text-primary-600 dark:text-primary-400">{member.email}</div>
                    <div className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500">{member.role}</div>
                  </div>
                  {canManageMembers && member.id !== project.creator?.id && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                      disabled={removeLoading}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                    >
                      Remover
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {canManageMembers && (
          <Card>
            <CardContent className="p-8">
              <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">
                Convites Pendentes
              </h3>
              <div className="space-y-3">
                {pendingInvitations.length === 0 ? (
                  <p className="text-sm text-primary-600 dark:text-primary-400">
                    Nenhum convite pendente
                  </p>
                ) : (
                  pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0"
                    >
                      <div>
                        <div className="text-primary-950 dark:text-white font-medium">
                          {invitation.email}
                        </div>
                        <div className="text-sm text-primary-600 dark:text-primary-400">
                          Convidado por {invitation.inviterName}
                        </div>
                        <div className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500">
                          Enviado em{" "}
                          {invitation.createdAt ? (
                            <DateDisplay date={invitation.createdAt} />
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                      <CancelProjectInvitationDialog
                        email={invitation.email}
                        onConfirm={() => handleCancelInvitation(invitation.id)}
                        disabled={cancelLoading}
                      />
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Criador do Projeto</h3>
            <div>
              <div className="text-primary-950 dark:text-white font-medium">{project.creator?.fullName}</div>
              <div className="text-sm text-primary-600 dark:text-primary-400">{project.creator?.email}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
